import React, { useState, useEffect, useRef } from "react"
import { chatService } from "../services/apichat"
import { useSocket } from "../contexts/SocketContext"
import MessageList from "./MessageList"
import MessageInput from "./MessageInput"
import Avatar from "./Avatar"
import { useReduxStore } from "../hooks/useReduxStore"

const ChatSystem = ({
  selectedUserId,
  isAdmin,
  onConversationUpdate,
  onMessageReceived,
  onMessageSent,
  onBack
}) => {
  const {getState}=useReduxStore()
  const {userData}=getState("AUTH")
  const {adminData,adminToken}=getState("ADMIN_AUTH")
  const [conversation, setConversation] = useState(null)
  const [messages, setMessages] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState("")
  const [typingUsers, setTypingUsers] = useState([])

  const { socket, joinConversation, leaveConversation } = useSocket()
  const user=Boolean(adminToken)?adminData:userData
  const messagesEndRef = useRef(null)

  useEffect(() => {
    if (selectedUserId) {
      initializeConversation()
    }
  }, [selectedUserId])

  useEffect(() => {
    if (socket) {
      // Join the conversation if it exists
      if (conversation) {
        joinConversation(conversation._id)
      }

      // Set up local event listeners for this specific conversation
      console.log(
        "🔌 Setting up LOCAL socket event listeners for conversation:",
        conversation?._id || "NEW"
      )

      const handleLocalReceiveMessage = data => {
        console.log(
          "📥 LOCAL receive_message for conversation:",
          conversation?._id || "NEW",
          data
        )

        // Only handle if this message belongs to current conversation
        if (
          data.message &&
          conversation &&
          data.message.conversationId === conversation._id
        ) {
          console.log(
            "📥 Message belongs to current conversation, adding to UI"
          )

          // Prevent duplicate messages
          setMessages(prev => {
            const messageExists = prev.some(msg => msg._id === data.message._id)
            if (messageExists) {
              console.log("⚠️ Duplicate message prevented:", data.message._id)
              return prev
            }
            return [...prev, data.message]
          })

          // Update conversation if provided
          if (data.conversation) {
            setConversation(data.conversation)
            if (onConversationUpdate) {
              onConversationUpdate(data.conversation)
            }
          }
        }
      }

      const handleLocalMessageSent = data => {
        console.log(
          "📤 LOCAL message_sent for conversation:",
          conversation?._id || "NEW",
          data
        )

        // Always handle message_sent events - this is the user's own message
        if (data.message) {
          console.log("📤 Adding user's own message to UI:", data.message._id)

          // Prevent duplicate messages
          setMessages(prev => {
            const messageExists = prev.some(msg => msg._id === data.message._id)
            if (messageExists) {
              console.log("⚠️ Duplicate message prevented:", data.message._id)
              return prev
            }
            return [...prev, data.message]
          })

          // Update conversation if provided (important for first message)
          if (data.conversation) {
            console.log(
              "📤 Updating conversation from message_sent:",
              data.conversation._id
            )
            setConversation(data.conversation)
            if (onConversationUpdate) {
              onConversationUpdate(data.conversation)
            }
          }
        }
      }

      const handleLocalUserTyping = data => {
        console.log(
          "⌨️ LOCAL user_typing for conversation:",
          conversation?._id || "NEW",
          data
        )

        // Only handle if this typing is for current conversation
        if (
          conversation &&
          data.conversationId === conversation._id &&
          data.userId !== user?._id
        ) {
          setTypingUsers(prev => [
            ...prev.filter(id => id !== data.userId),
            data.userId
          ])
        }
      }

      const handleLocalUserStoppedTyping = data => {
        console.log(
          "⌨️ LOCAL user_stopped_typing for conversation:",
          conversation?._id || "NEW",
          data
        )

        // Only handle if this typing is for current conversation
        if (conversation && data.conversationId === conversation._id) {
          setTypingUsers(prev => prev.filter(id => id !== data.userId))
        }
      }

      const handleMessageSeen = data => {
        console.log(
          "👁️ LOCAL message_seen for conversation:",
          conversation?._id || "NEW",
          data
        )

        // Only handle if this is for current conversation
        if (conversation && data.conversationId === conversation._id) {
          setMessages(prev =>
            prev.map(msg =>
              msg._id === data.messageId
                ? { ...msg, isSeen: true, seenAt: new Date().toISOString() }
                : msg
            )
          )
        }
      }

      // Set up local listeners
      socket.on("receive_message", handleLocalReceiveMessage)
      socket.on("message_sent", handleLocalMessageSent)
      socket.on("user_typing", handleLocalUserTyping)
      socket.on("user_stopped_typing", handleLocalUserStoppedTyping)
      socket.on("message_seen", handleMessageSeen)

      console.log(
        "✅ LOCAL socket event listeners set up for conversation:",
        conversation?._id || "NEW"
      )

      return () => {
        // Clean up local event listeners
        socket.off("receive_message", handleLocalReceiveMessage)
        socket.off("message_sent", handleLocalMessageSent)
        socket.off("user_typing", handleLocalUserTyping)
        socket.off("user_stopped_typing", handleLocalUserStoppedTyping)
        socket.off("message_seen", handleMessageSeen)

        // Leave conversation if it exists
        if (conversation) {
          leaveConversation(conversation._id)
        }
        console.log(
          "🧹 LOCAL socket event listeners cleaned up for conversation:",
          conversation?._id || "NEW"
        )
      }
    }
  }, [socket, conversation])

  useEffect(() => {
    scrollToBottom()
  }, [messages])

  // Handle messages from global events
  useEffect(() => {
    if (onMessageReceived) {
      // This will be called when DashboardPage receives a message
      // We need to check if this message belongs to current conversation
      console.log("📥 ChatSystem received message from global event")
    }
  }, [onMessageReceived])

  const initializeConversation = async () => {
    try {
      setLoading(true)
      setError("")

      console.log("Initializing conversation with user ID:", selectedUserId)

      let response
      if (isAdmin) {
        // Admin getting conversation with specific user
        response = await chatService.getAdminConversationWithUser(
          selectedUserId
        )
      } else {
        // User getting conversation with admin
        response = await chatService.getConversationWithAdmin()
      }

      console.log("Conversation response:", response)

      // Handle new API response structure: { success, status, message, data }
      if (response.success && response.data && response.data.conversation) {
        // Conversation exists
        console.log("✅ Conversation found:", response.data.conversation)
        setConversation(response.data.conversation)

        // Load messages
        console.log(
          "Loading messages for conversation:",
          response.data.conversation._id
        )
        const messagesResponse = await chatService.getMessages(
          response.data.conversation._id
        )
        console.log("Messages response:", messagesResponse)

        // Messages API returns: { success: true, data: { messages: [...], pagination: {...} } }
        if (
          messagesResponse.success &&
          messagesResponse.data &&
          messagesResponse.data.messages
        ) {
          const messages = messagesResponse.data.messages
          console.log("✅ Messages loaded:", messages.length)
          // Messages are already in correct order (oldest first) from API, no need to reverse
          setMessages(messages)
        } else {
          console.log(
            "❌ No messages found or unexpected response:",
            messagesResponse
          )
          setMessages([])
        }
      } else if (response.success && response.data === null) {
        // No conversation exists yet - this is normal for first-time chats
        console.log("ℹ️ No conversation found yet:", response.message)
        setConversation(null)
        setMessages([])
        // Don't set error - this is normal for new conversations
      } else {
        // Unexpected response structure
        console.log("❌ Unexpected conversation response:", response)
        setConversation(null)
        setMessages([])
        setError("Unexpected response from server")
      }
    } catch (error) {
      console.error("Error initializing conversation:", error)
      console.error("Error details:", {
        message: error?.message || "Unknown error",
        status: error?.response?.status,
        data: error?.response?.data
      })
      setError("Failed to load conversation")
    } finally {
      setLoading(false)
    }
  }

  const handleReceiveMessage = data => {
    console.log("📨 Received message:", data)

    // Prevent duplicate messages
    setMessages(prev => {
      const messageExists = prev.some(msg => msg._id === data.message._id)
      if (messageExists) {
        console.log("⚠️ Duplicate message prevented:", data.message._id)
        return prev
      }
      return [...prev, data.message]
    })

    // Handle conversation creation/update
    if (data.conversation) {
      console.log(
        "✅ Conversation created/updated via receive_message:",
        data.conversation
      )
      console.log(
        "📋 Conversation participants:",
        data.conversation.participants
      )
      setConversation(data.conversation)
      // Notify parent component about conversation update
      if (onConversationUpdate) {
        console.log("🔄 Notifying parent about conversation update")
        onConversationUpdate(data.conversation)
      }
    }
  }

  const handleMessageSent = data => {
    console.log("📤 Message sent confirmation:", data)

    // Prevent duplicate messages
    setMessages(prev => {
      const messageExists = prev.some(msg => msg._id === data.message._id)
      if (messageExists) {
        console.log("⚠️ Duplicate message prevented:", data.message._id)
        return prev
      }
      return [...prev, data.message]
    })

    // Handle conversation creation/update
    if (data.conversation) {
      console.log(
        "✅ Conversation created/updated via message_sent:",
        data.conversation
      )
      console.log(
        "📋 Conversation participants:",
        data.conversation.participants
      )
      setConversation(data.conversation)
      // Notify parent component about conversation update
      if (onConversationUpdate) {
        console.log("🔄 Notifying parent about conversation update")
        onConversationUpdate(data.conversation)
      }
    }
  }

  const handleUserTyping = data => {
    if (data.userId !== user?._id) {
      setTypingUsers(prev => [
        ...prev.filter(id => id !== data.userId),
        data.userId
      ])
    }
  }

  const handleUserStoppedTyping = data => {
    setTypingUsers(prev => prev.filter(id => id !== data.userId))
  }

  const handleMessageSeen = data => {
    setMessages(prev =>
      prev.map(msg =>
        msg._id === data.messageId
          ? { ...msg, isSeen: true, seenAt: new Date().toISOString() }
          : msg
      )
    )
  }

  const handleConversationUpdated = data => {
    console.log("✅ Conversation updated via conversation_updated event:", data)

    // Handle different data structures
    const conversationData = data.conversation || data

    if (conversationData) {
      setConversation(conversationData)
      // Notify parent component about conversation update
      if (onConversationUpdate) {
        console.log("🔄 Notifying parent about conversation update")
        onConversationUpdate(conversationData)
      }
    }
  }

  const handleMessageError = data => {
    console.error("Message error:", data.error)
    // Only show critical message errors, not mark as seen errors
    if (!data.error?.includes("mark") && !data.error?.includes("seen")) {
      setError(data.error)
    }
  }

  const handleMarkSeenSuccess = data => {
    console.log("Messages marked as seen successfully:", data)
    // data.conversationId - conversation ID
    // data.messagesCount - number of messages marked as seen
  }

  const handleMessagesSeen = data => {
    console.log("Messages seen by:", data)
    // data.conversationId - conversation ID
    // data.seenBy - user ID who marked as seen
    // data.seenAt - timestamp when marked as seen
    // data.messagesCount - number of messages marked as seen
  }

  const handleMarkSeenError = error => {
    console.error("Mark as seen error:", error)
    // Don't show error to user for mark as seen failures - it's not critical
    // setError('Failed to mark messages as seen');
  }

  const handleTypingError = error => {
    console.error("Typing error:", error)
    setError("Typing indicator error")
  }

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" })
  }

  // Debug function to test socket events
  const testSocketEvents = () => {
    console.log("🧪 Testing socket events...")
    console.log("🧪 Socket connected:", socket?.connected)
    console.log("🧪 Socket ID:", socket?.id)

    if (socket) {
      // Send test message
      const testMessage = {
        receiverId: isAdmin ? selectedUserId : "admin",
        messageType: "text",
        text: "Test message from frontend debug"
      }

      console.log("🧪 Sending test message:", testMessage)
      socket.emit("send_message", testMessage)
    } else {
      console.log("❌ No socket available for testing")
    }
  }

  // Add test function to window for debugging
  useEffect(() => {
    window.testSocketEvents = testSocketEvents
    window.socket = socket
    console.log(
      "🧪 Debug functions added to window: testSocketEvents(), socket"
    )
  }, [socket])

  const getChatTitle = () => {
    if (!conversation&&isAdmin) return ""

    if (isAdmin) {
      const otherUser = conversation.participants?.find(
        p => p._id !== user?._id
      )
      return otherUser ? `${otherUser.name}` : "Chat"
    } else {
      return "Meteorologist"
    }
  }

  if (loading) {
    return (
      <div
        style={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          // background: "#f8f9fa"
        }}
        className="bg-blueGray text-white"
      >
        <div className="loading">
          <div className="spinner"></div>
          <span style={{ marginLeft: "10px" }}>Loading conversation...</span>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div
        style={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "#f8f9fa",
          color: "#dc3545"
        }}
      >
        <div style={{ textAlign: "center" }}>
          <h3>Error</h3>
          <p>{error}</p>
          <button onClick={initializeConversation} className="btn btn-primary">
            Retry
          </button>
        </div>
      </div>
    )
  }

  return (
    <div
      className="flex-1 flex flex-col h-full w-full bg-[#212C43]"
      style={{
        height: "100vh", // Fixed height for the entire chat container
        maxHeight: "100vh",
      }}
    >
      {/* Chat Header */}
      <div
        className="flex items-center justify-between px-4 py-3 border-b border-borderGray shrink-0 h-[10vh]"
      >
        <div className="flex items-center gap-3">
          {/* Back Button for Mobile */}
          {onBack && (
            <button 
              onClick={onBack}
              className="lg:hidden text-white p-2 -ml-2 hover:bg-white/10 rounded-full transition-colors"
              aria-label="Back to list"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
          )}

          {/* Avatar */}
          <Avatar name={getChatTitle()} size="medium" />

          {/* Name and Status */}
          <div>
            <h3
              
              className="font-medium text-lg text-white leading-tight"
            >
              {getChatTitle()}
            </h3>
            {typingUsers.length > 0 ? (
              <p
              className="font-normal text-sm text-white/80"

              >
                {typingUsers.length === 1 ? "typing..." : "typing..."}
              </p>
            ) : (
              <div
                className="text-xs text-gray-400 mt-0.5"
              >
                {conversation?.participants?.find(p => p._id !== user?._id)
                  ?.isOnline ? (
                  <span className="text-green-500">online</span>
                ) : (
                  <span className="text-gray-400">last seen recently</span>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Action buttons */}
        {/* <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <button
            style={{
              background: "none",
              border: "none",
              padding: "8px",
              borderRadius: "50%",
              cursor: "pointer",
              color: "#666",
              fontSize: "16px"
            }}
          >
            ⋮
          </button>
        </div> */}
      </div>

      {/* Messages Area */}
      <div
        className="flex-1 flex flex-col overflow-y-auto px-2 sm:px-4 py-2"
      >
        <MessageList
          messages={messages}
          currentUserId={user?._id || user?.id || ""}
          conversationId={conversation?._id || ""}
        />

      </div>
      {/* Message Input */}
    <div
      className="shrink-0 sticky bottom-0 bg-[#212C43] w-full"
      >
    <MessageInput
        conversationId={conversation?._id || null}
        receiverId={selectedUserId}
        onMessageSent={() => {
          // Message will be handled by socket events
        }}
      /> 
    </div>
    </div>
  )
}

export default ChatSystem

